import { useTranslation } from 'react-i18next';
import { FileText, Minus } from 'lucide-react';
import { formatCurrency } from '../lib/dateFormat';

type EntryTypeTotal = {
  name: string;
  category: string;
  total: number;
};

type StatementCardProps = {
  incomeTypes: EntryTypeTotal[];
  expenseTypes: EntryTypeTotal[];
  totalIncome: number;
  totalExpenses: number;
  totalTithe: number;
  totalSabbathSchool: number;
};

export default function StatementCard({
  incomeTypes,
  expenseTypes,
  totalIncome,
  totalExpenses,
  totalTithe,
  totalSabbathSchool,
}: StatementCardProps) {
  const { t } = useTranslation();

  const currentBalance = totalIncome - totalExpenses;
  const totalChurch = currentBalance - totalTithe - totalSabbathSchool;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
          <FileText className="w-6 h-6 text-slate-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-800">{t('statement.title')}</h2>
      </div>

      <div className="mb-6 pb-6 border-b border-slate-200">
        <div className="flex items-baseline justify-between">
          <span className="text-base text-slate-600">{t('statement.currentBalance')}</span>
          <span
            className={`text-4xl font-bold ${
              currentBalance >= 0 ? 'text-emerald-600' : 'text-red-600'
            }`}
          >
            {formatCurrency(currentBalance)}
          </span>
        </div>
      </div>

      <div className="mb-6 pb-6 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('statement.incomeSources')}</h3>
        <div className="space-y-3">
          {incomeTypes.map((type) => (
            <div key={type.name} className="flex items-center justify-between">
              <span className="text-slate-700">{type.name}</span>
              <span className="text-lg font-semibold text-emerald-600">
                {formatCurrency(type.total)}
              </span>
            </div>
          ))}
          {incomeTypes.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-2">{t('statement.noIncome')}</p>
          )}
        </div>
        {incomeTypes.length > 0 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
            <span className="text-base font-semibold text-slate-700">{t('dashboard.totalIncome')}</span>
            <span className="text-xl font-bold text-emerald-600">{formatCurrency(totalIncome)}</span>
          </div>
        )}
      </div>

      <div className="mb-6 pb-6 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('statement.expenseItems')}</h3>
        <div className="space-y-3">
          {expenseTypes.map((type) => (
            <div key={type.name} className="flex items-center justify-between">
              <span className="text-slate-700">{type.name}</span>
              <span className="text-lg font-semibold text-red-600">
                {formatCurrency(type.total)}
              </span>
            </div>
          ))}
          {expenseTypes.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-2">{t('statement.noExpenses')}</p>
          )}
        </div>
        {expenseTypes.length > 0 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
            <span className="text-base font-semibold text-slate-700">{t('dashboard.totalExpenses')}</span>
            <span className="text-xl font-bold text-red-600">{formatCurrency(totalExpenses)}</span>
          </div>
        )}
      </div>

      <div className="bg-slate-50 rounded-lg p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4">{t('statement.totalChurch')}</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-700">{t('statement.startingBalance')}</span>
            <span
              className={`text-lg font-semibold ${
                currentBalance >= 0 ? 'text-emerald-600' : 'text-red-600'
              }`}
            >
              {formatCurrency(currentBalance)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Minus className="w-4 h-4 text-red-600" />
              <span className="text-slate-700">{t('statement.lessTithe')}</span>
            </div>
            <span className="text-lg font-semibold text-red-600">
              {formatCurrency(totalTithe)}
            </span>
          </div>

          <div className="flex items-center justify-between pb-4 border-b-2 border-slate-300">
            <div className="flex items-center gap-2">
              <Minus className="w-4 h-4 text-red-600" />
              <span className="text-slate-700">{t('statement.lessSabbathSchool')}</span>
            </div>
            <span className="text-lg font-semibold text-red-600">
              {formatCurrency(totalSabbathSchool)}
            </span>
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-base font-bold text-slate-800">{t('statement.totalChurch')}</span>
            <span
              className={`text-3xl font-bold ${
                totalChurch >= 0 ? 'text-sky-600' : 'text-orange-600'
              }`}
            >
              {formatCurrency(totalChurch)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
